/* global window */

import utils from './utils';
import uuid from 'uuid/v4';

export const TWO_HOURS_IN_MINUTES = '120';
export const TIME_TO_DISCONNECT_IN_MILLIES = 3 * 60 * 1000;

const shouldDisconnectVisitor = (lastActiveTimestamp, timeTillDisconnect) => {
  const diffMins = utils.diffMinutes(lastActiveTimestamp, new Date());
  return timeTillDisconnect && diffMins >= timeTillDisconnect;
};

const shouldResetTimestamp = (
  firebaseInitialLoading,
  lastDisconnectionTime,
) => {
  if (!firebaseInitialLoading) {
    const idleTime = Date.now() - lastDisconnectionTime;
    return idleTime > TIME_TO_DISCONNECT_IN_MILLIES;
  }
};

const PRESENCE_SUMMARY_PATH = 'presence-summary/';
const ENTERED_SITE_EVENT = 'entered';
const LEFT_SITE_EVENT = 'left';

class FirebaseChatService {
  timeTillDisconnect;
  visitorInstanceId;
  lastActiveTimestamp;

  private interval;
  private fb?: firebase.app.App;
  private db?: firebase.database.Database;
  private eventPath?: string;
  private presencePath?: string;
  private initialConnection = true;
  private firebaseInitialLoading = true;
  private readonly presenceState = {
    timestamp: '',
    lastSeenTimestamp: '',
    pageName: '',
    location: '',
    typing: false,
    deviceType: '',
  };
  private lastDisconnectionTime;
  private correlationId;
  private biToken;
  private visitorId;

  async init({
    authKey,
    options,
    eventPath,
    presencePath,
    visitorInstanceId,
    biToken,
    visitorId,
    shouldSetAuthToNone,
    correlationId,
  }) {
    this.eventPath = eventPath;
    this.presencePath = presencePath;
    this.visitorInstanceId = visitorInstanceId;
    this.correlationId = correlationId;
    this.biToken = biToken;
    this.visitorId = visitorId;
    this.timeTillDisconnect = TWO_HOURS_IN_MINUTES;
    this.lastActiveTimestamp = new Date();

    if (!this.initialConnection) {
      window.clearInterval(this.interval);
      this.interval = null;
      this.fb = undefined;
    }

    const firebase = require('firebase/app');
    require('firebase/auth');
    require('firebase/database');

    this.fb = firebase.initializeApp(options, uuid());
    const fb = this.fb as firebase.app.App;

    if (shouldSetAuthToNone) {
      await fb.auth().setPersistence(firebase.auth.Auth.Persistence.NONE);
    } else {
      await fb
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.SESSION);
    }

    await fb.auth().signInWithCustomToken(authKey);
    this.db = fb.database();
    this.setHeartbeat();
    this.initialConnection = false;
  }

  setLastSeenTimestamp() {
    const firebase = require('firebase/app');

    this.updatePresence({
      lastSeenTimestamp: firebase.database.ServerValue.TIMESTAMP,
    });
  }

  goOnlineIfOffline() {
    this.lastActiveTimestamp = new Date();
    if (!this.interval) {
      this.db && this.db.goOnline();
      this.setHeartbeat();
    }
  }

  goOffline() {
    this.db && this.db.goOffline();
  }

  setHeartbeat() {
    this.setLastSeenTimestamp();
    this.interval = setInterval(() => {
      if (
        shouldDisconnectVisitor(
          this.lastActiveTimestamp,
          this.timeTillDisconnect,
        )
      ) {
        window.clearInterval(this.interval);
        this.interval = null;
        this.goOffline();
      }
      this.setLastSeenTimestamp();
    }, 1000 * 30);
  }

  removeOnDisconnect() {
    this.db &&
      this.db
        .ref(this.presencePath)
        .onDisconnect()
        .remove();
  }

  private readonly siteVisitEvent = eventType => {
    const firebase = require('firebase/app');

    return {
      eventType,
      visitorInstanceId: this.visitorInstanceId,
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      correlationId: this.correlationId,
      biToken: this.biToken,
      visitorId: this.visitorId,
    };
  };

  reportPresenceSummaryEvents() {
    if (!this.db) {
      return;
    }

    //tslint:disable no-floating-promises prettier

    this.db
      .ref(`${PRESENCE_SUMMARY_PATH}${this.visitorInstanceId}|${ENTERED_SITE_EVENT}`)
      .set(this.siteVisitEvent(ENTERED_SITE_EVENT));

    this.db
      .ref(`${PRESENCE_SUMMARY_PATH}${this.visitorInstanceId}|${LEFT_SITE_EVENT}`)
      .onDisconnect()
      .set(this.siteVisitEvent(LEFT_SITE_EVENT));

    //tslint:enable no-floating-promises prettier
  }

  subscribeToEventsAndInfo({ eventCallback, timestamp }) {
    if (!this.db) {
      return;
    }

    const eventsRef = this.db
      .ref(this.eventPath)
      .orderByChild('timestamp')
      .startAt(new Date(timestamp).getTime());
    // Make sure we remove all previous listeners.
    eventsRef.off();
    this.subscribeToInfoEvents();
    eventsRef.on('child_added', snapshot => {
      if (snapshot) {
        const val = snapshot.val();
        eventCallback(typeof val === 'string' ? JSON.parse(val) : val);
      }
    });
    return this;
  }

  subscribeToInfoEvents() {
    if (!this.db) {
      return;
    }

    const connectionInfoRef = this.db.ref('.info/connected');
    connectionInfoRef.on('value', connectedSnap => {
      if (connectedSnap && connectedSnap.val() === true) {
        this.updatePresence(
          shouldResetTimestamp(
            this.firebaseInitialLoading,
            this.lastDisconnectionTime,
          ) // set state with all previous data +? new timestamp
            ? { timestamp: utils.getCurrentTimestamp() }
            : {},
        );
        this.removeOnDisconnect();
        this.firebaseInitialLoading = false;
      } else {
        this.lastDisconnectionTime = utils.getCurrentDate();
      }
    });
  }

  updatePresence(newState) {
    Object.assign(this.presenceState, newState);
    this.db && this.db.ref(this.presencePath).set(this.presenceState);
    return this;
  }

  isTyping(): boolean {
    return this.presenceState.typing;
  }
}

export default new FirebaseChatService();
